import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from 'sonner';
import { Mail, Save, Send, Loader2, ShieldAlert, ArrowLeft, BellRing } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function EmailSettingsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/email-settings');
      setData(res.data);
      setForm(res.data.settings);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load email settings');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setGroup = (k, v) => setForm(f => ({ ...f, recipient_groups: { ...f.recipient_groups, [k]: v } }));
  const setAlert = (k, patch) => setForm(f => ({ ...f, alerts: { ...f.alerts, [k]: { ...f.alerts[k], ...patch } } }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/admin/email-settings', {
        emails_enabled: form.emails_enabled,
        override_recipient: form.override_recipient || '',
        default_cc: form.default_cc || '',
        default_reply_to: form.default_reply_to || '',
        hr_reply_to: form.hr_reply_to || '',
        recipient_groups: form.recipient_groups,
        alerts: form.alerts,
      });
      setForm(res.data);
      toast.success('Email settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await api.post('/admin/email-settings/test', { to: testTo });
      toast.success(`Test email sent to ${res.data.to.join(', ')}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally { setTesting(false); }
  };

  if (loading || !form) {
    return <div className="p-8 text-center text-slate-400 text-sm">Loading email settings…</div>;
  }

  const env = data.env || {};
  const byModule = (data.alert_catalog || []).reduce((acc, a) => {
    (acc[a.module] = acc[a.module] || []).push(a); return acc;
  }, {});
  const disabledCount = Object.values(form.alerts || {}).filter(a => !a.enabled).length;

  return (
    <div className="p-4 sm:p-6 max-w-5xl space-y-5" data-testid="email-settings-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Button variant="ghost" size="sm" className="-ml-2 mb-1 text-slate-500" onClick={() => navigate('/admin/tools')} data-testid="email-settings-back">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Admin Tools
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><Mail className="w-5 h-5 text-indigo-600" /> Email Settings</h1>
          <p className="text-sm text-slate-500 mt-1">Default addresses and alert switches for all outbound ERP email. Admin only.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="email-settings-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save changes
        </Button>
      </div>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Sending</CardTitle>
          <CardDescription className="text-xs">
            Sender: <span className="font-mono">{env.sender_email}</span> ·{' '}
            {env.resend_configured
              ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Mail service connected</Badge>
              : <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">Mail service not configured</Badge>}
            {env.env_email_override ? <span className="ml-2 text-amber-700">Server override active: {env.env_email_override}</span> : null}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3 bg-slate-50/60">
            <div>
              <p className="text-sm font-medium">All outbound email</p>
              <p className="text-xs text-slate-500">Master switch — turn off to stop every email (in-app notifications continue).</p>
            </div>
            <Switch checked={!!form.emails_enabled} onCheckedChange={v => set('emails_enabled', v)} data-testid="email-master-switch" />
          </div>
          {!form.emails_enabled && (
            <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2.5" data-testid="email-disabled-warning">
              <ShieldAlert className="w-4 h-4 mt-0.5" /> Email sending is OFF. Reminders, PO mails, COC mails and HR notices will not leave the system.
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Default CC (every email)</Label>
              <Input value={form.default_cc || ''} onChange={e => set('default_cc', e.target.value)} placeholder="qa@bhpl.co.in, purchase@bhpl.co.in"
                className="h-8 text-xs mt-1 font-mono" data-testid="email-default-cc" />
            </div>
            <div>
              <Label className="text-xs">Default Reply-To</Label>
              <Input value={form.default_reply_to || ''} onChange={e => set('default_reply_to', e.target.value)} placeholder={env.env_default_reply_to || 'blank = triggering user'}
                className="h-8 text-xs mt-1 font-mono" data-testid="email-default-reply-to" />
            </div>
            <div>
              <Label className="text-xs">HR Reply-To</Label>
              <Input value={form.hr_reply_to || ''} onChange={e => set('hr_reply_to', e.target.value)} placeholder={env.env_hr_reply_to || 'hr@bhpl.co.in'}
                className="h-8 text-xs mt-1 font-mono" data-testid="email-hr-reply-to" />
            </div>
            <div>
              <Label className="text-xs">Test / override recipient</Label>
              <Input value={form.override_recipient || ''} onChange={e => set('override_recipient', e.target.value)} placeholder="Reroute ALL mail here (leave blank in production)"
                className="h-8 text-xs mt-1 font-mono" data-testid="email-override-recipient" />
              {form.override_recipient ? <p className="text-[10px] text-amber-700 mt-1">Every email will go only to this address.</p> : null}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-2 border-t pt-3">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Send a test email to</Label>
              <Input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="you@bhpl.co.in" className="h-8 text-xs mt-1 font-mono" data-testid="email-test-input" />
            </div>
            <Button variant="outline" size="sm" onClick={sendTest} disabled={testing || !testTo.trim()} data-testid="email-test-send">
              {testing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Send test
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Default email IDs by function</CardTitle>
          <CardDescription className="text-xs">Central address book — used when a module needs a standing recipient instead of an individual user.</CardDescription>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-3">
          {(data.recipient_groups || []).map(g => (
            <div key={g.key}>
              <Label className="text-xs">{g.label}</Label>
              <Input value={form.recipient_groups?.[g.key] || ''} onChange={e => setGroup(g.key, e.target.value)}
                placeholder="name@bhpl.co.in" className="h-8 text-xs mt-1 font-mono" data-testid={`email-group-${g.key}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><BellRing className="w-4 h-4 text-amber-600" /> Scheduled alerts</CardTitle>
          <CardDescription className="text-xs">
            Switch individual reminders on/off and add standing recipients (added as CC).
            {disabledCount > 0 && <span className="text-amber-700 ml-1">{disabledCount} currently off.</span>}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(byModule).map(([mod, alerts]) => (
            <div key={mod}>
              <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">{mod}</p>
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50/80">
                    <TableHead className="text-xs">Alert</TableHead>
                    <TableHead className="text-xs w-40">Schedule</TableHead>
                    <TableHead className="text-xs">Extra recipients (CC)</TableHead>
                    <TableHead className="text-xs w-20 text-right">On</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {alerts.map(a => (
                      <TableRow key={a.key} data-testid={`email-alert-row-${a.key}`}>
                        <TableCell className="text-sm">{a.label}</TableCell>
                        <TableCell className="text-[11px] text-slate-500 font-mono">{a.schedule}</TableCell>
                        <TableCell>
                          <Input value={form.alerts?.[a.key]?.extra_recipients || ''}
                            onChange={e => setAlert(a.key, { extra_recipients: e.target.value })}
                            placeholder="optional" className="h-7 text-xs font-mono" data-testid={`email-alert-cc-${a.key}`} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Switch checked={form.alerts?.[a.key]?.enabled !== false}
                            onCheckedChange={v => setAlert(a.key, { enabled: v })} data-testid={`email-alert-switch-${a.key}`} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {form.updated_at && (
        <p className="text-[11px] text-slate-400">Last updated {new Date(form.updated_at).toLocaleString()} by {form.updated_by || '—'}</p>
      )}
    </div>
  );
}
